import type { ComponentProps } from 'react';
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import {
  InputCombobox,
  type Props,
} from '#pie/components/input-combobox/InputCombobox';
import { InputField } from '#pie/components/input-field/InputField';
import { useExtendedRules } from '../formHelpers';

type FormComboboxProps<T extends FieldValues> = Omit<
  Props,
  'id' | 'isError' | 'required' | keyof ControllerRenderProps<T, Path<T>>
> &
  Pick<UseControllerProps<T>, 'disabled' | 'name' | 'rules' | 'defaultValue'> &
  Pick<ComponentProps<typeof InputField>, 'label' | 'className'> & {
    onChange?: (value: string) => void;
  };

export function FormCombobox<T extends FieldValues>({
  name,
  rules: originalRules,
  defaultValue,
  onChange,
  label,
  disabled,
  className,
  ...props
}: FormComboboxProps<T>) {
  const rules = useExtendedRules(originalRules);
  const {
    fieldState: { error },
    field: { onChange: onFieldChange, ...field },
  } = useController({
    defaultValue,
    name,
    rules,
  });

  return (
    <InputField
      name={name}
      isRequired={!!rules?.required}
      label={label}
      error={error?.message}
      className={className}
    >
      <InputCombobox
        label={label}
        isError={!!error}
        required={!!rules?.required}
        onChange={(value) => {
          onFieldChange({ target: { value } });
          onChange?.(value);
        }}
        {...field}
        disabled={disabled}
        {...props}
      />
    </InputField>
  );
}
